import { Box, Container, Paper, Skeleton } from "@mui/material";
import { tableColumnProps, currencies } from "common/constants";
import { dateToString, extractPhotos } from "common/utils";
import CustomButton from "components/buttons/CustomButton/CustomButton";
import CustomText from "components/common/CustomText/CustomText";
import ImageCarousel from "components/modals/ImageCarousel/ImageCarousel";
import ProductSuggestionActionButtons from "components/tables/ActionButtons/ProductSuggestionActionButtons";
import CustomCellLabel from "components/tables/AllUsers/CustomCellLabel";
import CustomCellValue from "components/tables/AllUsers/CustomCellValue";
import BeautyProductsFilters from "components/tables/Filters/BeautyProductsFilters";
import SmallImagesList from "components/tables/SmallImagesList/SmallImagesList";
import { useGetBeautyProducts } from "hooks/useBeautyProducts";
import FilterIcon from "icons/FilterIcon";
import ProductsIconOutline from "icons/ProductsIconOutline";
import { useState, useEffect } from "react";
import { colors } from "styles/colors";
import useGlobalStyles from "styles/globalStyles";
import { ListBeautyProductsDto } from "types/dto";
import { BeautyProductsFilterDto } from "types/dto/BeautyProductsFilterDto";
import { IPhotoSize } from "types/types";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import BeautyProductActionButtons from "components/tables/ActionButtons/BeautyProductActionButtons";


export default function BeautyProductsTable() {
  const globalStyles = useGlobalStyles();
  const [carouselImages, setCarouselImages] = useState<IPhotoSize[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);

  const [filters, setFilters] = useState<BeautyProductsFilterDto>({});

  const requestParams: ListBeautyProductsDto = {
    page: page + 1,
    size,
    ...filters,
  };

  const { data, refetch, isLoading, isRefetching } = useGetBeautyProducts(requestParams, {
    onSuccess: (data: any) => { },
    onError: () => console.log('error'),
  });


  const columns: GridColDef[] = [
    {
      field: 'barcode',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Barcode" />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={params.row.code} />
          </Box >
        )
      }
    },
    {
      field: 'images',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Images" />
      ),
      renderCell: (params) => {
        const images = extractPhotos(params.row)
        return (
          <SmallImagesList
            images={images}
            setCarouselImages={setCarouselImages}
            setInitialIndex={setCarouselIndex}
            setCarouselOpen={setCarouselOpen}
          />
        )
      }
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Actions" />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <BeautyProductActionButtons
              productData={params.row}
            />
          </Box>
        )
      }
    },
  ];

  useEffect(() => {
    if (data?.count !== undefined) {
      setRowCount(data.count);
    }
  }, [data?.count]);


  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <ProductsIconOutline size={24} color={colors.primary} />
            </Box>
            <CustomText text="All Suggestions" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
          <Box display={'flex'} gap={2}>
            <CustomButton
              text='Filter'
              variant='outlined'
              height={40}
              icon={<FilterIcon />}
              onClick={() => setFiltersOpen(prev => !prev)}
            />
          </Box>
        </Box>

        <BeautyProductsFilters
          open={filtersOpen}
          initialValues={filters}
          loading={isLoading}
          onApply={(values) => {
            setFilters(values);
            setPage(0);
            refetch();
          }}
          onReset={() => {
            setFilters({});
            setPage(0);
            refetch();
          }}
        />

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          // checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(selection) => {
            console.log('Selected row IDs:', selection);
          }}
          rows={data?.products || []}
          getRowId={(row) => row.code}
          columns={columns}
          loading={isLoading || isRefetching}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          className={globalStyles.dataGrid}
        />
      </Paper>
      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        initialIndex={carouselIndex}
        onClose={() => setCarouselOpen(false)}
      />

      <Box mt={3} />
    </Container>
  );
}
